import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { Shield, Clock, XCircle } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useBusiness } from '../../context/BusinessContext';

function VerificationBanner() {
  const { selectedBusiness } = useBusiness();
  const navigate = useNavigate();

  // Don't show banner if no business selected
  if (!selectedBusiness?.business?.verification) {
    return null;
  }

  const verification = selectedBusiness.business.verification;
  const businessId = selectedBusiness.business._id;

  // Don't show banner if verified
  if (verification.isVerified || verification.verificationStatus === 'approved') {
    return null;
  }

  // Determine banner color and text based on status
  let backgroundColor = '#ef4192'; // Solid brand pink for pending
  let textColor = '#ffffff'; // White text
  let message = '';
  let buttonText = '';
  let icon = <Shield size={20} weight="fill" />;

  if (verification.verificationStatus === 'under_review') {
    backgroundColor = '#4caf50'; // Green for under review
    textColor = '#ffffff'; // White text
    message = 'Your verification is under review. This typically takes 1-3 business days.';
    buttonText = 'View Status';
    icon = <Clock size={20} weight="fill" />;
  } else if (verification.verificationStatus === 'rejected') {
    backgroundColor = '#f44336'; // Red for rejected
    textColor = '#ffffff'; // White text
    message = verification.rejectionReason 
      ? `Verification rejected: ${verification.rejectionReason}`
      : 'Your verification was rejected. Please review and resubmit.';
    buttonText = 'Review & Resubmit';
    icon = <XCircle size={20} weight="fill" />;
  } else {
    // pending
    backgroundColor = '#ef4192'; // Solid brand pink
    textColor = '#ffffff'; // White text
    message = 'Your business is not yet verified. Please start your verification to get seen by couples.';
    buttonText = (() => {
      const businessDetails = selectedBusiness.business.businessDetails;
      const hasStarted = businessDetails && businessDetails.businessType;
      return hasStarted ? 'Continue Verification' : 'Start Verification';
    })();
    icon = <Shield size={20} weight="fill" />;
  }

  const handleClick = () => {
    navigate(`/supplier/verification/${businessId}`);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1100,
        backgroundColor: backgroundColor,
        borderTop: `2px solid rgba(255, 255, 255, 0.3)`,
        py: { xs: 1, sm: 1.5 },
        px: { xs: 2, sm: 3 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: { xs: 1, sm: 2 },
        boxShadow: '0 -2px 4px rgba(0,0,0,0.1)',
        flexWrap: { xs: 'wrap', sm: 'nowrap' }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flex: 1, minWidth: 0 }}>
        <Box sx={{ color: textColor, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          {icon}
        </Box>
        <Typography
          variant="body2"
          sx={{
            color: textColor,
            fontWeight: 500,
            flex: 1,
            fontSize: { xs: '0.75rem', sm: '0.875rem' }
          }}
        >
          {message}
        </Typography>
      </Box>
      <Button
        variant="outlined"
        size="small"
        onClick={handleClick}
        sx={{
          borderColor: 'white',
          color: 'white',
          backgroundColor: 'transparent',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
            borderColor: 'white'
          },
          minWidth: 'auto',
          px: { xs: 1.5, sm: 2 },
          py: 0.5,
          fontSize: { xs: '0.75rem', sm: '0.875rem' },
          textTransform: 'none',
          fontWeight: 600,
          flexShrink: 0,
          whiteSpace: 'nowrap'
        }}
      >
        {buttonText}
      </Button>
    </Box>
  );
}

export default VerificationBanner;















